<template>
  <div
    class="relative rounded-md border border-background-fg/8"
  >
    <div
      :class="[
        props.highlight
          ? 'bg-primary text-primary-fg'
          : 'bg-background text-background-fg',
        'absolute inset-y-0 left-0 flex w-10 items-center justify-center rounded-l-md border-r border-background-fg/8',
      ]"
    >
      <span class="font-semibold">
        {{ merchantCurrencySymbol }}
      </span>
    </div>
    <input
      ref="inputRef"
      :disabled="disabled"
      type="text"
      :name="name"
      :class="[
        'block w-full rounded-md bg-background-fg/5 pl-12 text-sm font-semibold focus:outline-none sm:text-sm',

        props.highlight
          ? 'border-primary ring-primary focus:border-primary focus:ring-primary'
          : hasError
            ? 'border-error text-error placeholder:text-error focus:border-error focus:ring-error'
            : 'border-none focus:border-primary focus:ring-primary',
      ]"
      :placeholder="placeholder"
      :value="disabled ? '' : formattedValue"
      :aria-invalid="hasError"
      enterKeyHint="done"
      @keydown.backspace="handleBackspace"
      @keydown.enter="handleEnter"
    />
  </div>
</template>

<script setup lang="ts">
import {
  useCurrencyInput,
  CurrencyDisplay,
  ValueScaling,
} from "vue-currency-input"

const props = withDefaults(
  defineProps<{
    highlight?: boolean
    name?: string
    modelValue: number
    hasError?: boolean
    placeholder?: string
    currency?: string
    disabled?: boolean
    min?: number
  }>(),
  {
    highlight: false,
    name: undefined,
    label: "",
    placeholder: "",
    currency: "GBP",
    disabled: false,
    min: undefined,
  }
)

// Auto-emitted by useCurrencyInput
const emit = defineEmits<{
  (event: "update:modelValue", value: number): void
  (event: "change", value: number): void
}>()

const { numberValue, formattedValue, inputRef, setValue } =
  useCurrencyInput({
    currency: props.currency,
    currencyDisplay: CurrencyDisplay.hidden,
    precision: 2,
    autoDecimalDigits: true,
    hideCurrencySymbolOnFocus: false,
    hideGroupingSeparatorOnFocus: false,
    hideNegligibleDecimalDigitsOnFocus: false,
    valueScaling: ValueScaling.precision,
    valueRange: {
      min: props.min,
    },
  })

const { merchantCurrencySymbol } = useCurrency()

watch(
  () => props.modelValue,
  (value_) => {
    setValue(value_)
  },
  { immediate: true }
)

const handleBackspace = (event: KeyboardEvent) => {
  if (numberValue.value === 0) {
    event.preventDefault()
  }
}

const handleEnter = (event: Event) =>
  (event?.target as HTMLInputElement)?.blur()
</script>
